import { useCallback, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { PublicHeader } from "@/components/public/PublicHeader";
import { PublicFooter } from "@/components/public/PublicFooter";
import { PublicContainer } from "@/components/common/PublicContainer";
import { LegalSearch } from "@/components/legal/LegalSearch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const HEADER_SCROLL_MARGIN = "scroll-mt-24";

/**
 * PublicHeader is `h-16` (64px). IntersectionObserver.rootMargin only
 * accepts px or % — rem/em/vh throw in the browser.
 */
export const LEGAL_SECTION_OBSERVER = Object.freeze({
  root: null,
  rootMargin: "-64px 0px -55% 0px",
  threshold: Object.freeze([0, 0.25, 0.5, 1]),
});

function TableOfContents({ sections, activeId, onNavigate, className }) {
  return (
    <nav className={cn("flex flex-col gap-1", className)} aria-label="Índice da página">
      {sections.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          onClick={(event) => {
            event.preventDefault();
            onNavigate(id);
          }}
          className={cn(
            "rounded-xl px-3 py-2 text-sm transition-colors",
            activeId === id
              ? "bg-white text-black"
              : "text-zinc-400 hover:bg-zinc-900 hover:text-white",
          )}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

export function LegalSection({ id, title, children }) {
  return (
    <section
      id={id}
      className={cn(HEADER_SCROLL_MARGIN, "mb-14 last:mb-8")}
      aria-labelledby={`${id}-heading`}
    >
      <h2
        id={`${id}-heading`}
        className="text-lg sm:text-xl font-light text-white mb-5 tracking-tight"
      >
        {title}
      </h2>
      <div className="space-y-4 text-zinc-300 leading-relaxed text-sm sm:text-base [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_a]:text-white [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-zinc-200">
        {children}
      </div>
    </section>
  );
}

/**
 * Layout editorial de leitura para páginas legais (termos, privacidade).
 *
 * Scroll natural da página (como a Central de Ajuda): header sticky,
 * hero + busca + conteúdo + footer no fluxo do documento.
 * TOC desktop permanece sticky; IntersectionObserver usa a viewport.
 */
export function LegalPageLayout({
  title,
  kicker,
  description,
  contextLabel,
  legalDocument,
  sections,
  children,
  lastUpdated,
  version,
}) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");
  const [tocOpen, setTocOpen] = useState(false);

  const scrollToSection = useCallback((id) => {
    const el = window.document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
    setTocOpen(false);
  }, []);

  useEffect(() => {
    const ids = sections.map((section) => section.id);
    const elements = ids
      .map((id) => window.document.getElementById(id))
      .filter(Boolean);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      LEGAL_SECTION_OBSERVER,
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div
      className="flex min-h-dvh flex-col bg-[#050505] text-zinc-300 fade-in"
      data-testid="legal-page"
    >
      <PublicHeader context="legal" contextLabel={contextLabel} />

      <main className="w-full min-w-0 flex-1">
        <PublicContainer className="w-full min-w-0 pt-8 pb-12 sm:pt-10 sm:pb-16">
          <header data-testid="legal-page-hero">
            {kicker ? (
              <p
                className="text-sm uppercase tracking-wider text-zinc-500"
                data-testid="legal-page-kicker"
              >
                {kicker}
              </p>
            ) : null}
            <h1
              className="mt-3 text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl"
              data-testid="legal-page-title"
            >
              {title}
            </h1>
            {description ? (
              <p
                className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base"
                data-testid="legal-page-description"
              >
                {description}
              </p>
            ) : null}
            {(lastUpdated || version) && (
              <p className="mt-5 text-sm text-zinc-500">
                {version ? (
                  <span data-testid="legal-page-version">Versão {version}</span>
                ) : null}
                {version && lastUpdated ? (
                  <span className="text-zinc-600"> · </span>
                ) : null}
                {lastUpdated ? (
                  <span data-testid="legal-page-updated">
                    Atualizado em {lastUpdated}
                  </span>
                ) : null}
              </p>
            )}
          </header>

          {legalDocument ? (
            <div className="mt-8 max-w-2xl sm:mt-10">
              <LegalSearch document={legalDocument} onSelectSection={scrollToSection} />
            </div>
          ) : null}

          <div className="mt-8 lg:hidden sm:mt-10">
            <Collapsible open={tocOpen} onOpenChange={setTocOpen}>
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3 text-sm text-white">
                Índice
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-zinc-400 transition-transform duration-200",
                    tocOpen && "rotate-180",
                  )}
                  aria-hidden
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 rounded-xl border border-zinc-800 bg-zinc-900/20 p-2">
                <TableOfContents
                  sections={sections}
                  activeId={activeId}
                  onNavigate={scrollToSection}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>

          <div className="mt-8 flex min-w-0 w-full flex-col gap-8 sm:mt-10 lg:flex-row lg:gap-12 xl:gap-14">
            <aside className="hidden lg:block w-60 xl:w-64 shrink-0 sticky top-20 self-start">
              <p className="mb-3 text-xs uppercase tracking-wider text-zinc-500">
                Índice
              </p>
              <TableOfContents
                sections={sections}
                activeId={activeId}
                onNavigate={scrollToSection}
              />
            </aside>

            <article
              className="min-w-0 flex-1 max-w-3xl"
              data-testid="legal-page-content"
              tabIndex={-1}
            >
              {children}
            </article>
          </div>
        </PublicContainer>
      </main>

      <PublicFooter context="legal" />
    </div>
  );
}
